import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Column, EmptyState } from "@hightouch/htui";
import { useUser } from "src/contexts/user-context";
import {
  type SpaceAudienceTemplateAccessMode,
  useSpacesForUserQuery,
} from "src/graphql";
import { switchSpace } from "src/utils/workspaces";
import {
  isPathAccessibleInSpace,
  isPathOrChildrenAccessibleInSpace,
} from "./path-access-registry";
import { noop } from "lodash";

export interface Space {
  id: string;
  name: string;
  audienceTemplateAccessMode: SpaceAudienceTemplateAccessMode;
}

export interface SpaceContextValue {
  isSpacesEnabled: boolean;
  selectedSpace: Space | null;
  availableSpaces: Space[];
  isInSpace: boolean;
  isLoadingSpace: boolean;
  isUserSpaceScoped: boolean;
  selectSpace: (space: Space | null) => void;
  isPathAccessibleInSpace: (path: string) => boolean;
  isPathOrChildrenAccessibleInSpace: (path: string) => boolean;
}

const SpaceContext = createContext<SpaceContextValue | null>(null);

export interface SpaceProviderProps {
  children: ReactNode;
}

export const SpaceProvider: FC<SpaceProviderProps> = ({ children }) => {
  const { user, workspace } = useUser();
  const isSpacesEnabled = workspace?.spaces_enabled ?? false;

  // Fetches spaces via the space_user_groups relationship.
  const { data: spacesWithGroups } = useSpacesForUserQuery(
    {},
    {
      enabled: isSpacesEnabled,
    },
  );

  // Session is source of truth - backend persists and validates space on each request
  const currentSpaceId = user?.space?.current_space_id ?? null;
  const isUserSpaceScoped = user?.space?.is_user_space_scoped ?? false;

  // Hasura permissions filter spaces based on user's group assignments
  const availableSpaces: Space[] = useMemo(() => {
    if (!spacesWithGroups?.spaces) return [];

    return spacesWithGroups.spaces.map((s) => ({
      id: String(s.id),
      name: s.name,
      audienceTemplateAccessMode:
        s.audience_template_access_mode === "selected" ? "selected" : "all",
    }));
  }, [spacesWithGroups?.spaces]);

  const selectedSpace = useMemo(() => {
    if (!currentSpaceId) return null;
    return availableSpaces.find((s) => s.id === String(currentSpaceId)) ?? null;
  }, [currentSpaceId, availableSpaces]);

  // Sync to window.Hightouch for API request headers (uses session value for immediate availability)
  useEffect(() => {
    window.Hightouch = {
      ...window.Hightouch,
      spaceId: currentSpaceId ?? null,
    };
  }, [currentSpaceId]);

  const handleSelectSpace = useCallback((space: Space | null): void => {
    switchSpace(space?.id ?? null);
  }, []);

  const isInSpace = Boolean(currentSpaceId);
  const isLoadingSpace = isInSpace && selectedSpace === null;

  const value: SpaceContextValue = useMemo(
    () =>
      isSpacesEnabled
        ? {
            isSpacesEnabled: true,
            selectedSpace,
            availableSpaces,
            isInSpace,
            isLoadingSpace,
            isUserSpaceScoped,
            selectSpace: handleSelectSpace,
            isPathAccessibleInSpace,
            isPathOrChildrenAccessibleInSpace,
          }
        : {
            isSpacesEnabled: false,
            selectedSpace: null,
            availableSpaces: [],
            isInSpace: false,
            isLoadingSpace: false,
            isPathAccessibleInSpace: () => true,
            isPathOrChildrenAccessibleInSpace: () => true,
            isUserSpaceScoped: false,
            selectSpace: noop,
          },
    [
      isSpacesEnabled,
      selectedSpace,
      availableSpaces,
      isInSpace,
      isLoadingSpace,
      isUserSpaceScoped,
      handleSelectSpace,
    ],
  );

  // Edge case: space-scoped user with no space access (backend should prevent this)
  if (isSpacesEnabled && isUserSpaceScoped && availableSpaces.length === 0) {
    return (
      <Column align="center" justify="center" height="100vh" px={4}>
        <EmptyState
          title="No space access"
          message="You don't have access to any spaces in this workspace. Contact your admin for access."
        />
      </Column>
    );
  }

  return (
    <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>
  );
};

export function useSpaceContext(): SpaceContextValue {
  const context = useContext(SpaceContext);
  if (!context) {
    throw new Error("useSpaceContext must be used within a SpaceProvider");
  }
  return context;
}
